import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { getAuthSessionEpoch, useAuthStore } from '@/stores/authStore';
import { InstitutionalOrderStatusError, readInstitutionalOrderStatus, type InstitutionalOrderStatus } from '@/lib/polymarketUs/institutionalOrderStatusClient';
import type { InstitutionalDeskOrder } from '@/lib/polymarketUs/institutionalOrdersClient';
import type { useInstitutionalDeskOrders } from './useInstitutionalDeskOrders';
const subscribe = (notify: () => void) => useAuthStore.subscribe(notify);
type State = { base: string; orderId: string; row: string; busy: boolean; failed: boolean; data: InstitutionalOrderStatus | null };
export function useInstitutionalOrderStatus(desk: ReturnType<typeof useInstitutionalDeskOrders>, rows: readonly InstitutionalDeskOrder[], orgKey: string) {
  const epoch = useSyncExternalStore(subscribe, getAuthSessionEpoch);
  const accountId = desk.account?.id ?? '', scope = desk.data?.scope, snapshot = desk.data?.snapshot;
  const base = `${epoch}|${orgKey}|${accountId}|${scope?.scopeKey}|${scope?.revision}|${snapshot?.observedAtMs}`;
  const [state,setState] = useState<State | null>(null);
  const active = state?.base === base && rows.some(row => row.orderId === state.orderId && JSON.stringify(row) === state.row) ? state : null;
  const sequence = useRef(0), controller = useRef<AbortController | null>(null), busy = useRef(false);
  const current = useRef({ base, rows });
  useLayoutEffect(() => { current.current = { base, rows }; });
  if (state !== null && active === null) setState(null);
  function close() { sequence.current++; controller.current?.abort(); busy.current = false; setState(null); }
  const hasActive = active !== null;
  useEffect(() => {
    if (!hasActive) { sequence.current++; controller.current?.abort(); busy.current = false; }
  }, [base, hasActive]); // Clear departing account/scope/row without issuing a read.
  useEffect(() => () => { controller.current?.abort(); }, []);
  async function check(order: InstitutionalDeskOrder) {
    if (busy.current || desk.data?.canCheckStatus !== true || !scope || !snapshot || !rows.includes(order)) return;
    const row = JSON.stringify(order), own = new AbortController(), seq = ++sequence.current;
    controller.current?.abort(); controller.current = own; busy.current = true;
    setState({ base, row, orderId: order.orderId, busy: true, failed: false, data: null });
    const valid = () => seq === sequence.current && !own.signal.aborted && getAuthSessionEpoch() === epoch && current.current.base === base
      && current.current.rows.some(r => r.orderId === order.orderId && JSON.stringify(r) === row);
    try {
      const data = await readInstitutionalOrderStatus({ accountId, orderId: order.orderId, scope,
        snapshotObservedAtMs: snapshot.observedAtMs }, AbortSignal.any([own.signal,AbortSignal.timeout(40_000)]));
      if (valid()) setState({ base, row, orderId: order.orderId, busy: false, failed: false, data });
    } catch (error) {
      if (valid()) {
        setState({ base, row, orderId: order.orderId, busy: false, failed: true, data: null });
        if (error instanceof InstitutionalOrderStatusError && error.accessChanged) desk.invalidateAccess();
      }
    } finally { if (seq === sequence.current) busy.current = false; }
  }
  return { current: active, close, check, canCheck: desk.data?.canCheckStatus === true && !active?.busy };
}
